/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { css, StyleSheet } from 'aphrodite';
import { composeValidators, Dropdown, isEmptyValue, TextInput, Validator } from '@blend/enterprise-components';

import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { WrappedInput } from 'blb-components/WrappedInput';
import { buttonReset } from 'blb-styles/mixins';
import { BlendBridge } from 'blb-types/blendBridge';
import { FinalFormInputConfig } from 'blb-ui-types/helpers';
import { emailValidator } from 'blb-utils/validators';
import { FormTask, handleFormSubmit } from './FormTask';
import { Task } from './Task';
import { TaskModal } from '../TaskModal';

const styles = StyleSheet.create({
  firstName: {
    paddingRight: 8,
  },
  input: {
    marginTop: 8,
  },
  lastName: {
    paddingLeft: 8,
  },
  names: {
    display: 'flex',
  },
  result: {
    ...buttonReset(),
    flex: 1,
    padding: 8,
    textAlign: 'inherit',
  },
});

const getRoleSelectConfig = (options: BlendBridge.API.RoleOption[]): FinalFormInputConfig<Dropdown.Props, string> => ({
  component: WrappedInput,
  customComponentProps: {
    disabled: !options.length,
    id: 'it-role-select',
    options,
  },
  inputComponent: Dropdown,
  label: 'Role',
  labelSize: 'sm',
  name: 'partyType',
  placeholder: 'Select one',
  validate: isEmptyValue(
    !options.length
      ? 'This closing cannot be shared with anyone.'
      : 'Please select the role of the person you are sharing with.',
  ),
});

const inputValidator = (inputType: string, inputLabel: string): Validator => {
  if (inputType === 'email') {
    return composeValidators(isEmptyValue(`${inputLabel} required`), emailValidator);
  }

  return isEmptyValue(`${inputLabel} required`);
};

const getInputConfig = ({
  formChange,
  label,
  name,
  type,
  value,
  setSelectedResult,
}: {
  formChange: any;
  label: string;
  name: string;
  setSelectedResult: React.Dispatch<React.SetStateAction<ShareResult>>;
  type: 'text' | 'email';
  value?: string;
}): FinalFormInputConfig<TextInput.Props, string> => ({
  component: WrappedInput,
  customComponentProps: {
    onChange: e => {
      // Prevents React from resetting its properties:
      e.persist();
      const value = e.target.value;
      setSelectedResult((prev?: ShareResult): ShareResult => ({ ...prev, [name]: value }));
      formChange(name, value);
    },
    type,
    value,
  },
  inputComponent: TextInput,
  label,
  labelSize: 'sm',
  name,
  validate: inputValidator(type, label),
});

interface ShareResult {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export namespace ShareTask {
  export interface Props extends Omit<BlendBridge.API.ShareTask, 'type'> {
    onSuccess?: () => Promise<any>;
  }
}

// NOTE: this task is not generic due to limitations with displayConfigs and the
// dynamic nature of the UI asssociated with this task. We will make this task
// more generic as needed but until we know what requirements there are for
// similar tasks, we will keep things simple.
export const ShareTask: React.FC<ShareTask.Props> = ({
  buttonLabel,
  disabledReason,
  isDisabled,
  onSuccess,
  roleOptions,
  taskHeader,
  taskId,
  taskSubheader,
}) => {
  const [selectedResult, setSelectedResult] = useState<ShareResult>({});
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const Toaster = useToasts();

  const handleClose = () => {
    setError(undefined);
    setModalOpen(false);
  };

  // Monkey patch onSuccess in order to show a success toast
  const onShareSuccess = async () => {
    await onSuccess!();
    handleClose();
    Toaster!.addToast({
      message: 'To see all the users you’ve shared the closing package with, go to the Contacts tab.',
      theme: 'info',
    });
  };

  const onSubmit = (formValues: FormTask.FormValues) =>
    handleFormSubmit({
      formValues,
      handleClose,
      onSuccess: onShareSuccess,
      setError,
      setLoading,
      taskId,
    });

  return (
    <Task
      buttonLabel={buttonLabel}
      disabledReason={disabledReason}
      displayType={'link'}
      isDisabled={isDisabled}
      onClick={() => setModalOpen(true)}
      taskHeader={taskHeader}
      taskId={taskId}
      type={BlendBridge.TaskTypes.SHARE}
    >
      <Form onSubmit={onSubmit}>
        {({ form: { change }, handleSubmit }) => (
          <TaskModal
            divideFooter={true}
            error={error}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            isOpen={isModalOpen}
            subTitle={taskSubheader}
            submitButtonLabel="Share"
            taskId={taskId}
            title={taskHeader}
          >
            <div className={css(styles.names)}>
              <Field
                {...getInputConfig({
                  formChange: change,
                  label: 'First Name',
                  name: 'firstName',
                  setSelectedResult,
                  type: 'text',
                  value: selectedResult.firstName,
                })}
                containerStyles={[styles.input, styles.firstName]}
              />
              <Field
                {...getInputConfig({
                  formChange: change,
                  label: 'Last Name',
                  name: 'lastName',
                  setSelectedResult,
                  type: 'text',
                  value: selectedResult.lastName,
                })}
                containerStyles={[styles.input, styles.lastName]}
              />
            </div>

            <Field
              {...getInputConfig({
                formChange: change,
                label: 'Email',
                name: 'email',
                setSelectedResult,
                type: 'email',
                value: selectedResult.email,
              })}
              containerStyles={[styles.input]}
            />
            {roleOptions && (
              <div className={css(styles.input)}>
                <Field {...getRoleSelectConfig(roleOptions)} />
              </div>
            )}
          </TaskModal>
        )}
      </Form>
    </Task>
  );
};
